// Pipeline calculation utilities for the Bullogic Command Center
// All pipeline stages, service tiers, and revenue projection logic

// Master list of all possible stages.
// expectedDays = how long a healthy deal typically stays in this stage.
// After expectedDays * 2, decay starts pulling the probability down toward floor.
export const PIPELINE_STAGES = [
  { id: 'discovery-scheduled', label: 'Discovery Scheduled', shortLabel: 'Disc. Sched.', probability: 0.05, order: 1, expectedDays: 7 },
  { id: 'discovery-complete', label: 'Discovery Complete', shortLabel: 'Disc. Comp.', probability: 0.15, order: 2, expectedDays: 14 },
  { id: 'strategy-scheduled', label: 'Strategy Scheduled', shortLabel: 'Strat. Sched.', probability: 0.40, order: 3, expectedDays: 14 },
  { id: 'strategy-complete', label: 'Strategy Complete', shortLabel: 'Strat. Comp.', probability: 0.60, order: 4, expectedDays: 14 },
  { id: 'proposal', label: 'Proposal', shortLabel: 'Proposal', probability: 0.80, order: 5, expectedDays: 14 },
  { id: 'closed-won', label: 'Closed Won', shortLabel: 'Won', probability: 1.00, order: 6, expectedDays: 0 },
  { id: 'closed-lost', label: 'Closed Lost', shortLabel: 'Lost', probability: 0.00, order: 7, expectedDays: 0 },
  { id: 'unqualified', label: 'Unqualified', shortLabel: 'Unqual.', probability: 0.00, order: 8, expectedDays: 0 },
];

/**
 * Compute smart probability for a prospect.
 * Starts with the stage base probability, then applies a time-based decay
 * if the deal has been in its current stage longer than expected.
 *
 * Formula:
 *   - If days <= 2 * expectedDays: full base probability
 *   - Beyond that: linear decay down to a floor of 50% of base
 *   - Floor reached at days = 6 * expectedDays
 *
 * @param {Object} prospect - prospect with stage and stageHistory
 * @param {Date|string} now - reference date (default: today)
 * @returns {number} probability between 0 and 1
 */
export function calculateSmartProbability(prospect, now = new Date()) {
  const stage = PIPELINE_STAGES.find(s => s.id === prospect.stage);
  if (!stage) return 0;

  const base = stage.probability;
  // Closed states or 0-probability stages: no decay
  if (base === 0 || base === 1 || !stage.expectedDays) return base;

  // Find days in current stage
  const currentEntry = prospect.stageHistory?.find(h => h.stageId === prospect.stage && !h.exitedDate);
  const enteredDate = currentEntry?.enteredDate || prospect.createdDate;
  if (!enteredDate) return base;

  const daysInStage = Math.floor((new Date(now) - new Date(enteredDate)) / (1000 * 60 * 60 * 24));
  const expected = stage.expectedDays;

  // Healthy zone: no decay
  if (daysInStage <= expected * 2) return base;

  // Decay zone: linear from base down to base * 0.5 between 2x and 6x expected
  const decayProgress = Math.min(1, (daysInStage - expected * 2) / (expected * 4));
  const floor = base * 0.5;
  return base - (base - floor) * decayProgress;
}

/**
 * Get effective probability for a prospect.
 * Uses manual override if set, otherwise smart probability with day-based decay.
 * @returns {{ probability: number, isManual: boolean, base: number }}
 */
export function getEffectiveProbability(prospect, sourceStatsOrNow, maybeNow) {
  // Back-compat: callers used to call getEffectiveProbability(prospect)
  // or getEffectiveProbability(prospect, now). New shape is
  // getEffectiveProbability(prospect, sourceStats, now). Sniff the second
  // arg: if it's a Date, treat it as `now` and skip sourceStats.
  let sourceStats = null;
  let now = new Date();
  if (sourceStatsOrNow instanceof Date) {
    now = sourceStatsOrNow;
  } else if (sourceStatsOrNow && typeof sourceStatsOrNow === 'object') {
    sourceStats = sourceStatsOrNow;
    if (maybeNow instanceof Date) now = maybeNow;
  }

  const stage = PIPELINE_STAGES.find(s => s.id === prospect.stage);
  const base = stage?.probability || 0;
  const isManual = typeof prospect.manualProbability === 'number' && prospect.manualProbability >= 0;
  const probability = getProspectExpectedProbability(prospect, sourceStats, now);
  return { probability, isManual, base };
}

// Division-specific stage paths
// BWM: full 5-stage pipeline (discovery > strategy > proposal)
// BTS: 3-stage pipeline (discovery > proposal, no strategy meetings)
// Default days-to-close per division, used when not enough closed-won data exists
export const DEFAULT_DAYS_TO_CLOSE = { bwm: 60, bts: 30, bia: 14 };

/**
 * Compute observed average days-to-close per division from won deals.
 * Falls back to DEFAULT_DAYS_TO_CLOSE if a division has fewer than 2 won deals.
 *
 * @param {Array} prospects
 * @returns {{ bwm: number, bts: number, bia: number, hasData: { bwm: boolean, bts: boolean, bia: boolean } }}
 */
export function computeAvgDaysToClose(prospects) {
  const result = { ...DEFAULT_DAYS_TO_CLOSE };
  const hasData = { bwm: false, bts: false, bia: false };
  if (!Array.isArray(prospects)) return { ...result, hasData };

  const buckets = { bwm: [], bts: [], bia: [] };
  for (const p of prospects) {
    if (p.outcome !== 'won') continue;
    if (!p.createdDate || !p.closedDate) continue;
    const div = p.division || SERVICE_TIERS[p.serviceTier]?.division;
    if (!buckets[div]) continue;
    const created = new Date(p.createdDate);
    const closed = new Date(p.closedDate);
    const days = Math.round((closed - created) / (1000 * 60 * 60 * 24));
    if (days >= 0 && days < 365) buckets[div].push(days);
  }

  for (const div of Object.keys(buckets)) {
    if (buckets[div].length >= 2) {
      const avg = buckets[div].reduce((s, v) => s + v, 0) / buckets[div].length;
      result[div] = Math.round(avg);
      hasData[div] = true;
    }
  }
  return { ...result, hasData };
}

// BIA: 2-stage pipeline (proposal only, no meetings - we run the numbers internally)
export const DIVISION_STAGES = {
  bwm: ['discovery-scheduled', 'discovery-complete', 'strategy-scheduled', 'strategy-complete', 'proposal'],
  bts: ['discovery-scheduled', 'discovery-complete', 'proposal'],
  bia: ['proposal'],
};

// Get the active stages for a division (excludes closed/unqualified)
export function getStagesForDivision(division) {
  const stageIds = DIVISION_STAGES[division] || DIVISION_STAGES.bwm;
  return stageIds.map(id => PIPELINE_STAGES.find(s => s.id === id)).filter(Boolean);
}

// Get the next stage for a prospect based on their division's stage path
export function getNextStageForProspect(prospect) {
  const division = prospect.division || SERVICE_TIERS[prospect.serviceTier]?.division || 'bwm';
  const path = DIVISION_STAGES[division] || DIVISION_STAGES.bwm;
  const currentIdx = path.indexOf(prospect.stage);

  if (currentIdx === -1) return PIPELINE_STAGES.find(s => s.id === path[0]) || null;
  if (currentIdx >= path.length - 1) return PIPELINE_STAGES.find(s => s.id === 'closed-won');
  return PIPELINE_STAGES.find(s => s.id === path[currentIdx + 1]) || null;
}

export const SERVICE_TIERS = {
  'guided-wealth': { division: 'bwm', label: 'Bullogic Guided Wealth', defaultMonthly: 650, defaultOneTime: 0, hasAUM: false, revenueType: 'recurring' },
  'total-wealth': { division: 'bwm', label: 'Bullogic Total Wealth', defaultMonthly: 1200, defaultOneTime: 7500, hasAUM: true, revenueType: 'recurring+onetime' },
  'tax-plan': { division: 'bwm', label: 'Tax Plan', defaultMonthly: 0, defaultOneTime: 5000, hasAUM: false, revenueType: 'onetime' },
  'investment-only': { division: 'bwm', label: 'Investment Only', defaultMonthly: 0, defaultOneTime: 0, hasAUM: true, revenueType: 'aum' },
  'tax-individual': { division: 'bts', label: 'Tax Prep (Individual)', defaultMonthly: 0, defaultOneTime: 1500, hasAUM: false, revenueType: 'onetime' },
  'tax-business': { division: 'bts', label: 'Tax Prep (Business)', defaultMonthly: 0, defaultOneTime: 3000, hasAUM: false, revenueType: 'onetime' },
  // Ongoing business tax/accounting retainer — recurring monthly, no AUM,
  // no one-time. Unlike the per-filing tax-prep tiers, this is a
  // subscription relationship.
  'business-package': { division: 'bts', label: 'Business Package', defaultMonthly: 0, defaultOneTime: 0, hasAUM: false, revenueType: 'recurring' },
  'insurance': { division: 'bia', label: 'Insurance', defaultMonthly: 0, defaultOneTime: 0, hasAUM: false, revenueType: 'commission' },
};

export const LEAD_SOURCES = [
  { id: 'cross-sell', label: 'Cross-Sell', defaultWeight: 0.85 },
  { id: 'referral', label: 'Referral', defaultWeight: 0.70 },
  { id: 'solicitor', label: 'Solicitor (Zoe Financial)', defaultWeight: 0.60 },
  { id: 'coi', label: 'COI/Partner', defaultWeight: 0.50 },
  { id: 'event', label: 'Event', defaultWeight: 0.30 },
  { id: 'website', label: 'Website', defaultWeight: 0.25 },
  { id: 'podcast', label: 'Podcast', defaultWeight: 0.20 },
  { id: 'youtube', label: 'YouTube', defaultWeight: 0.15 },
  { id: 'social', label: 'Social Media', defaultWeight: 0.15 },
  { id: 'other', label: 'Other', defaultWeight: 0.20 },
];

export const LOSS_REASONS = [
  { id: 'unresponsive', label: 'Unresponsive' },
  { id: 'price', label: 'Price' },
  { id: 'unqualified', label: 'Unqualified' },
  { id: 'competitor', label: 'Chose Competitor' },
  { id: 'timing', label: 'Not the Right Time' },
  { id: 'other', label: 'Other' },
];

// Default growth targets by division (50% overall = $836,768 for 2026)
export const DEFAULT_GROWTH_TARGETS = {
  overall: 0.50,  // 50% YoY
  bwm: 0.50,
  bts: 0.50,
  bia: 1.00,  // 100% for small/new division
};

// Minimum closed deals per source before we trust observed data over defaults
const MIN_SOURCE_SAMPLE_SIZE = 5;

/**
 * Calculate days between two ISO date strings.
 * If date2 is null/undefined, uses today (2026-04-17 or actual current date).
 */
export function daysBetween(date1, date2) {
  if (!date1) return 0;
  const d1 = new Date(date1);
  const d2 = date2 ? new Date(date2) : new Date();
  const diffMs = Math.abs(d2.getTime() - d1.getTime());
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

/**
 * Calculate annualized value from a prospect's estimatedValue object.
 * Formula: oneTime + (monthlyRecurring * 12) + (aum * aumFeeRate)
 */
export function calculateAnnualizedValue(estimatedValue) {
  if (!estimatedValue) return 0;
  const oneTime = estimatedValue.oneTime || 0;
  const monthlyRecurring = estimatedValue.monthlyRecurring || 0;
  const aum = estimatedValue.aum || 0;
  const aumFeeRate = estimatedValue.aumFeeRate || 0;
  return oneTime + (monthlyRecurring * 12) + (aum * aumFeeRate);
}

// Helper: derive division from prospect (stored field or from service tier)
function getProspectDivision(prospect) {
  if (prospect.division) return prospect.division;
  const tier = SERVICE_TIERS[prospect.serviceTier];
  return tier?.division || 'other';
}

/**
 * Get the source weight for a prospect.
 * Checks for observed data at the source+division level first (most specific),
 * then source-only level, then falls back to the default weight.
 * @param {Object} prospect - the prospect
 * @param {Object} sourceStats - pre-computed stats from calculateSourceStats()
 * @returns {number} source weight between 0 and 1
 */
export function getSourceWeight(prospect, sourceStats) {
  const sourceId = prospect.source || 'other';
  const division = getProspectDivision(prospect);
  const sourceDef = LEAD_SOURCES.find(s => s.id === sourceId);
  const defaultWeight = sourceDef?.defaultWeight || 0.20;

  if (!sourceStats) return defaultWeight;

  // First try: source + division specific (most accurate)
  const divKey = `${sourceId}:${division}`;
  if (sourceStats.bySourceDivision?.[divKey]?.totalOutcomes >= MIN_SOURCE_SAMPLE_SIZE) {
    return sourceStats.bySourceDivision[divKey].winRate;
  }

  // Second try: source only (aggregated across divisions)
  if (sourceStats.bySource?.[sourceId]?.totalOutcomes >= MIN_SOURCE_SAMPLE_SIZE) {
    return sourceStats.bySource[sourceId].winRate;
  }

  return defaultWeight;
}

/**
 * Calculate win rate stats per lead source, both aggregated and per division.
 * Returns {
 *   bySource: { [sourceId]: { won, lost, totalOutcomes, winRate, isObserved } },
 *   bySourceDivision: { [sourceId:division]: { won, lost, totalOutcomes, winRate, isObserved, division, sourceLabel } },
 * }
 */
export function calculateSourceStats(prospects) {
  if (!prospects) return { bySource: {}, bySourceDivision: {} };

  function makeEntry(label, defaultWeight) {
    return {
      won: 0,
      lost: 0,
      unqualified: 0,
      totalOutcomes: 0,            // won + lost (qualified decisions only)
      qualifiedTotal: 0,           // ever qualified (active + closed)
      decided: 0,                  // qualified + unqualified (qualify/disqualify decisions)
      total: 0,                    // every prospect from this source
      winRate: defaultWeight,      // won / totalOutcomes (with seed default)
      qualificationRate: null,     // qualified / decided
      isObserved: false,           // true once we have ≥ MIN_SOURCE_SAMPLE_SIZE outcomes
      label,
    };
  }

  const bySource = {};
  for (const source of LEAD_SOURCES) {
    bySource[source.id] = makeEntry(source.label, source.defaultWeight);
  }

  const bySourceDivision = {};

  for (const p of prospects) {
    const sourceId = p.source || 'other';
    const division = getProspectDivision(p);
    const divKey = `${sourceId}:${division}`;

    if (!bySource[sourceId]) bySource[sourceId] = makeEntry(sourceId, 0.20);
    if (!bySourceDivision[divKey]) {
      const sourceDef = LEAD_SOURCES.find(s => s.id === sourceId);
      bySourceDivision[divKey] = {
        ...makeEntry(sourceDef?.label || sourceId, sourceDef?.defaultWeight || 0.20),
        division,
        sourceId,
        sourceLabel: sourceDef?.label || sourceId,
      };
    }

    bySource[sourceId].total++;
    bySourceDivision[divKey].total++;

    if (wasQualified(p)) {
      bySource[sourceId].qualifiedTotal++;
      bySource[sourceId].decided++;
      bySourceDivision[divKey].qualifiedTotal++;
      bySourceDivision[divKey].decided++;
    }

    if (p.stage === 'unqualified') {
      bySource[sourceId].unqualified++;
      bySource[sourceId].decided++;
      bySourceDivision[divKey].unqualified++;
      bySourceDivision[divKey].decided++;
    }

    if (p.stage === 'closed-won') {
      bySource[sourceId].won++;
      bySource[sourceId].totalOutcomes++;
      bySourceDivision[divKey].won++;
      bySourceDivision[divKey].totalOutcomes++;
    } else if (p.stage === 'closed-lost') {
      bySource[sourceId].lost++;
      bySource[sourceId].totalOutcomes++;
      bySourceDivision[divKey].lost++;
      bySourceDivision[divKey].totalOutcomes++;
    }
  }

  // Compute rates where we have enough data. winRate measures sales
  // effectiveness on qualified prospects; qualificationRate measures
  // source quality (does this source send us fits).
  for (const s of Object.values(bySource)) {
    if (s.totalOutcomes >= MIN_SOURCE_SAMPLE_SIZE) {
      s.winRate = s.won / s.totalOutcomes;
      s.isObserved = true;
    }
    if (s.decided > 0) s.qualificationRate = s.qualifiedTotal / s.decided;
  }
  for (const s of Object.values(bySourceDivision)) {
    if (s.totalOutcomes >= MIN_SOURCE_SAMPLE_SIZE) {
      s.winRate = s.won / s.totalOutcomes;
      s.isObserved = true;
    }
    if (s.decided > 0) s.qualificationRate = s.qualifiedTotal / s.decided;
  }

  return { bySource, bySourceDivision };
}

/**
 * Calculate probability-weighted value for a prospect.
 * Uses the unified getProspectExpectedProbability so this number ALWAYS
 * matches what the revenue forecast actually projects.
 *
 * @param {Object} prospect - the prospect
 * @param {Object} sourceStats - optional pre-computed source stats. When
 *   provided, Discovery-Scheduled prospects get adjusted by source-level
 *   qualification rate (when enough data exists).
 */
export function calculateWeightedValue(prospect, sourceStats) {
  if (!prospect) return 0;
  const annualized = calculateAnnualizedValue(prospect.estimatedValue);
  return annualized * getProspectExpectedProbability(prospect, sourceStats);
}

/**
 * Single source of truth for "what's the probability this prospect closes won".
 * Order of precedence:
 *   1. Manual override (user explicitly set a probability)
 *   2. Stage probability with smart day-based decay
 *   3. For Discovery Scheduled only: adjusted by source qualification rate
 *      (sources that qualify well get bumped up, poor-qualifying sources
 *      get bumped down). Capped to [0.3x, 1.5x] of stage default to avoid
 *      extremes from small samples.
 *
 * Used by both calculateWeightedValue (headline KPI) and projectPipelineRevenue
 * (monthly forecast) so the two never drift.
 */
export function getProspectExpectedProbability(prospect, sourceStats, now = new Date()) {
  if (!prospect) return 0;

  // Manual override always wins.
  if (typeof prospect.manualProbability === 'number' && prospect.manualProbability >= 0) {
    return Math.max(0, Math.min(1, prospect.manualProbability));
  }

  const stage = PIPELINE_STAGES.find(s => s.id === prospect.stage);
  if (!stage) return 0;
  if (stage.id === 'closed-won') return 1;
  if (stage.id === 'closed-lost' || stage.id === 'unqualified') return 0;

  // Smart-decayed stage probability — handles stale prospects.
  let prob = calculateSmartProbability(prospect, now);

  // Discovery Scheduled gets a source-quality adjustment. Other stages
  // already imply qualification (Discovery Complete and later), so source
  // quality matters less — stage probability dominates.
  if (stage.id === 'discovery-scheduled' && sourceStats) {
    const sourceId = prospect.source || 'other';
    const division = getProspectDivision(prospect);
    const divKey = `${sourceId}:${division}`;
    const QUAL_BASELINE = 0.5;  // assumed firm-wide qualification rate when we have no data

    let observedQualRate = null;
    const sd = sourceStats.bySourceDivision?.[divKey];
    const s = sourceStats.bySource?.[sourceId];
    if (sd?.qualificationRate != null && (sd.decided || 0) >= MIN_SOURCE_SAMPLE_SIZE) {
      observedQualRate = sd.qualificationRate;
    } else if (s?.qualificationRate != null && (s.decided || 0) >= MIN_SOURCE_SAMPLE_SIZE) {
      observedQualRate = s.qualificationRate;
    }

    if (observedQualRate != null) {
      const multiplier = Math.max(0.3, Math.min(1.5, observedQualRate / QUAL_BASELINE));
      prob *= multiplier;
    }
  }

  return Math.max(0, Math.min(1, prob));
}

/**
 * Determine if a prospect is in an active (non-terminal) stage.
 * Closed-won, closed-lost, AND unqualified are all terminal — they don't
 * count toward active pipeline value, days-in-pipeline, etc.
 */
function isActive(prospect) {
  return prospect.stage !== 'closed-won'
    && prospect.stage !== 'closed-lost'
    && prospect.stage !== 'unqualified';
}

/**
 * Has this prospect ever been qualified? Per the firm's process, a prospect
 * is qualified by the end of the Discovery call — so reaching Discovery
 * Complete (or any later non-terminal stage, plus closed-won/lost which
 * imply having been qualified) counts. Unqualified explicitly does not.
 */
function wasQualified(prospect) {
  if (!prospect?.stage) return false;
  if (prospect.stage === 'unqualified') return false;
  const stage = PIPELINE_STAGES.find(s => s.id === prospect.stage);
  if (!stage) return false;
  // Discovery Complete is order 2; closed-won/lost are 6/7 (also qualified).
  return stage.order >= 2;
}

/**
 * Calculate high-level pipeline KPIs from an array of prospects.
 */
export function calculatePipelineKPIs(prospects) {
  if (!prospects || prospects.length === 0) {
    return {
      totalProspects: 0,
      totalActive: 0,
      qualifiedCount: 0,
      qualifiedActive: 0,
      qualifiedHistorical: 0,
      unqualifiedCount: 0,
      qualificationRate: null,
      unweightedValue: 0,
      weightedValue: 0,
      avgDaysInPipeline: null,
      winRate: null,
      qualifiedWinRate: null,
      closedWonCount: 0,
      closedLostCount: 0,
    };
  }

  const sourceStats = calculateSourceStats(prospects);

  const active = prospects.filter(isActive);
  const closedWon = prospects.filter(p => p.stage === 'closed-won');
  const closedLost = prospects.filter(p => p.stage === 'closed-lost');
  const unqualified = prospects.filter(p => p.stage === 'unqualified');

  // Currently in qualified pipeline (Discovery Complete and later, but
  // still active — not yet closed/unqualified).
  const qualifiedActive = active.filter(wasQualified).length;

  // Historical qualified: every prospect we've ever determined was a fit.
  // Includes active qualified + closed-won + closed-lost. Excludes
  // unqualified (definitionally) and Discovery Scheduled (not yet decided).
  const qualifiedHistorical = prospects.filter(wasQualified).length;

  // Qualification rate: of prospects we've made a qualify/disqualify
  // decision on, what % qualified. Denominator excludes still-pending
  // (Discovery Scheduled) so it converges to a stable number.
  const decided = qualifiedHistorical + unqualified.length;
  const qualificationRate = decided > 0 ? qualifiedHistorical / decided : null;

  const unweightedValue = active.reduce((sum, p) => sum + calculateAnnualizedValue(p.estimatedValue), 0);
  const weightedValue = active.reduce((sum, p) => sum + calculateWeightedValue(p, sourceStats), 0);

  // Average days in pipeline. Terminal stages (won/lost/unqualified) end
  // the timer; active prospects use today as the implicit end.
  const TERMINAL = new Set(['closed-won', 'closed-lost', 'unqualified']);
  const allWithDays = prospects.filter(p => p.createdDate);
  let avgDaysInPipeline = null;
  if (allWithDays.length > 0) {
    const totalDays = allWithDays.reduce((sum, p) => {
      const endDate = TERMINAL.has(p.stage) ? p.closedDate : null;
      return sum + daysBetween(p.createdDate, endDate);
    }, 0);
    avgDaysInPipeline = Math.round(totalDays / allWithDays.length);
  }

  // Headline win rate: of all closed deals (won + lost — NOT unqualified),
  // what % won. By definition any closed-lost was previously qualified
  // (we wouldn't have pursued otherwise), so this also equals the
  // "win rate of qualified prospects who reached a decision".
  const totalOutcomes = closedWon.length + closedLost.length;
  const winRate = totalOutcomes > 0 ? closedWon.length / totalOutcomes : null;
  const qualifiedWinRate = winRate; // alias for clarity at call sites

  return {
    totalProspects: prospects.length,
    totalActive: active.length,
    // Backward-compat alias: PipelineSnapshot + PipelineKPIRow read this.
    qualifiedCount: qualifiedActive,
    qualifiedActive,
    qualifiedHistorical,
    unqualifiedCount: unqualified.length,
    qualificationRate,
    unweightedValue,
    weightedValue,
    avgDaysInPipeline,
    winRate,
    qualifiedWinRate,
    closedWonCount: closedWon.length,
    closedLostCount: closedLost.length,
  };
}

/**
 * Calculate conversion rates between adjacent pipeline stages.
 * For each stage pair (N -> N+1), compute what % of prospects that entered
 * stage N have advanced to stage N+1 or beyond.
 * Returns null for stages with fewer than 3 data points.
 */
/**
 * Calculate conversion rates between adjacent stages.
 * @param {Array} prospects - array of prospects (optionally pre-filtered by division)
 * @param {string} division - optional division to determine which stage path to use
 */
export function calculateConversionRates(prospects, division) {
  if (!prospects || prospects.length === 0) return [];

  // Use division-specific stages if provided
  const stageIds = division ? (DIVISION_STAGES[division] || DIVISION_STAGES.bwm) : null;
  const activeStages = stageIds
    ? stageIds.map(id => PIPELINE_STAGES.find(s => s.id === id)).filter(Boolean)
    : PIPELINE_STAGES.filter(s => s.order >= 1 && s.order <= 5);

  return activeStages.map((fromStage, idx) => {
    if (idx >= activeStages.length - 1) return null;
    const toStage = activeStages[idx + 1];

    const enteredFrom = prospects.filter(p =>
      p.stageHistory && p.stageHistory.some(h => h.stageId === fromStage.id)
    );

    if (enteredFrom.length < 3) {
      return {
        fromStageId: fromStage.id,
        fromLabel: fromStage.label,
        toStageId: toStage.id,
        toLabel: toStage.label,
        rate: null,
        sampleSize: enteredFrom.length,
      };
    }

    const advanced = enteredFrom.filter(p =>
      p.stageHistory && p.stageHistory.some(h => {
        const hStage = PIPELINE_STAGES.find(s => s.id === h.stageId);
        return hStage && hStage.order >= toStage.order;
      })
    );

    return {
      fromStageId: fromStage.id,
      fromLabel: fromStage.label,
      toStageId: toStage.id,
      toLabel: toStage.label,
      rate: advanced.length / enteredFrom.length,
      sampleSize: enteredFrom.length,
    };
  }).filter(Boolean);
}

/**
 * Calculate average days spent in each stage from stageHistory.
 * Only considers history entries with both enteredDate and exitedDate.
 * Returns null for stages with no completed data.
 */
/**
 * @param {Array} prospects - optionally pre-filtered by division
 * @param {string} division - optional division to determine which stages to measure
 */
export function calculateStageVelocity(prospects, division) {
  if (!prospects || prospects.length === 0) return [];

  const stageIds = division ? (DIVISION_STAGES[division] || DIVISION_STAGES.bwm) : null;
  const activeStages = stageIds
    ? stageIds.map(id => PIPELINE_STAGES.find(s => s.id === id)).filter(Boolean)
    : PIPELINE_STAGES.filter(s => s.order >= 1 && s.order <= 5);

  return activeStages.map(stage => {
    // Collect all completed stints in this stage
    const completedStints = [];
    for (const p of prospects) {
      if (!p.stageHistory) continue;
      for (const h of p.stageHistory) {
        if (h.stageId === stage.id && h.enteredDate && h.exitedDate) {
          completedStints.push(daysBetween(h.enteredDate, h.exitedDate));
        }
      }
    }

    return {
      stageId: stage.id,
      label: stage.label,
      avgDays: completedStints.length > 0
        ? Math.round(completedStints.reduce((s, d) => s + d, 0) / completedStints.length)
        : null,
      sampleSize: completedStints.length,
    };
  });
}

/**
 * Summarize each active (non-closed) stage: count, total value, weighted value.
 */
export function calculateStageSummary(prospects) {
  if (!prospects) return [];

  const sourceStats = calculateSourceStats(prospects);
  const activeStages = PIPELINE_STAGES.filter(s => s.order >= 1 && s.order <= 5);

  return activeStages.map(stage => {
    const inStage = prospects.filter(p => p.stage === stage.id);
    return {
      stageId: stage.id,
      label: stage.label,
      count: inStage.length,
      totalValue: inStage.reduce((sum, p) => sum + calculateAnnualizedValue(p.estimatedValue), 0),
      weightedValue: inStage.reduce((sum, p) => sum + calculateWeightedValue(p, sourceStats), 0),
    };
  });
}

/**
 * Project monthly revenue from active pipeline prospects.
 * Returns array of 12 numbers (index 0 = Jan, 11 = Dec) representing
 * projected new monthly revenue, weighted by stage probability.
 *
 * Revenue recognition rules:
 * - Tax prep (individual/business): revenue recognized in April, or close month if after April
 * - Total Wealth one-time: 50% at close month, 50% three months later
 * - Recurring monthly (Guided/Total): spread from close month through December
 * - All values multiplied by the prospect's stage probability
 */
export function projectPipelineRevenue(prospects) {
  const monthly = new Array(12).fill(0);

  if (!prospects || prospects.length === 0) return monthly;

  const sourceStats = calculateSourceStats(prospects);
  // Observed avg days-to-close per division (with 2+ won deals); falls back
  // to DEFAULT_DAYS_TO_CLOSE for thin divisions.
  const daysToClose = computeAvgDaysToClose(prospects);
  const active = prospects.filter(isActive);
  const today = new Date();

  for (const prospect of active) {
    const stage = PIPELINE_STAGES.find(s => s.id === prospect.stage);
    if (!stage) continue;
    // Single source of truth: respects manual overrides, smart decay, AND
    // qualification-rate adjustment for Discovery Scheduled.
    const probability = getProspectExpectedProbability(prospect, sourceStats, today);

    const ev = prospect.estimatedValue || {};
    const monthlyRecurring = ev.monthlyRecurring || 0;
    const oneTime = ev.oneTime || 0;
    const aum = ev.aum || 0;
    const aumFeeRate = ev.aumFeeRate || 0;

    // Determine the month and YEAR revenue is expected to start. In order:
    //   1. serviceStartDate from the prospect (most accurate for tax-prep
    //      deals signed mid-year for next year's filing).
    //   2. Manual expectedCloseDate from the user.
    //   3. createdDate + observed days-to-close for the prospect's division.
    //   4. today + observed days-to-close (no createdDate available).
    // Then year-gate: if revenue starts outside the projection year, skip
    // entirely. Without this, getMonth() year-strips and the deal pollutes
    // the wrong year's forecast (e.g., Apr 2027 → Apr 2026).
    const PROJECTION_YEAR = 2026;
    let closeDate;
    if (prospect.serviceStartDate) {
      closeDate = new Date(prospect.serviceStartDate);
    } else if (prospect.expectedCloseDate) {
      closeDate = new Date(prospect.expectedCloseDate);
    } else {
      const division = getProspectDivision(prospect);
      const days = daysToClose[division] || DEFAULT_DAYS_TO_CLOSE[division] || 60;
      closeDate = prospect.createdDate ? new Date(prospect.createdDate) : new Date(today);
      closeDate.setDate(closeDate.getDate() + days);
    }
    if (closeDate.getFullYear() !== PROJECTION_YEAR) continue;
    const closeMonth = closeDate.getMonth();

    const tier = SERVICE_TIERS[prospect.serviceTier];
    if (!tier) continue;

    const revenueType = tier.revenueType;

    // Tax prep: recognize in April (month 3) or close month if after April
    if (revenueType === 'onetime') {
      const recognitionMonth = Math.max(closeMonth, 3); // April = 3
      if (recognitionMonth < 12) {
        monthly[recognitionMonth] += oneTime * probability;
      }
    }

    // Total Wealth has both recurring and one-time components
    if (revenueType === 'recurring+onetime') {
      // One-time: 50% at close, 50% three months later
      if (oneTime > 0) {
        if (closeMonth < 12) {
          monthly[closeMonth] += (oneTime * 0.5) * probability;
        }
        const secondHalf = closeMonth + 3;
        if (secondHalf < 12) {
          monthly[secondHalf] += (oneTime * 0.5) * probability;
        }
      }

      // Monthly recurring: spread from close month through December
      if (monthlyRecurring > 0) {
        for (let m = closeMonth; m < 12; m++) {
          monthly[m] += monthlyRecurring * probability;
        }
      }

      // AUM fee: annualized fee spread monthly from close month through December
      if (aum > 0 && aumFeeRate > 0) {
        const monthlyAumFee = (aum * aumFeeRate) / 12;
        for (let m = closeMonth; m < 12; m++) {
          monthly[m] += monthlyAumFee * probability;
        }
      }
    }

    // Guided Wealth (recurring only)
    if (revenueType === 'recurring') {
      if (monthlyRecurring > 0) {
        for (let m = closeMonth; m < 12; m++) {
          monthly[m] += monthlyRecurring * probability;
        }
      }

      // AUM fee if applicable
      if (aum > 0 && aumFeeRate > 0) {
        const monthlyAumFee = (aum * aumFeeRate) / 12;
        for (let m = closeMonth; m < 12; m++) {
          monthly[m] += monthlyAumFee * probability;
        }
      }
    }

    // Commission (insurance) - no standard recognition pattern, skip for now
  }

  // Round all values to nearest dollar
  return monthly.map(v => Math.round(v));
}

/**
 * Get the next stage object in the pipeline, or null if at a terminal stage.
 */
export function getNextStage(currentStageId) {
  const current = PIPELINE_STAGES.find(s => s.id === currentStageId);
  if (!current) return null;
  // Closed stages are terminal
  if (current.id === 'closed-won' || current.id === 'closed-lost') return null;
  // Find next by order
  const next = PIPELINE_STAGES.find(s => s.order === current.order + 1);
  return next || null;
}
